import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, PaperPlaneTilt, PencilSimple, DownloadSimple,
    EnvelopeOpen, Cursor, Warning, CheckCircle, Eye, XCircle,
} from "@phosphor-icons/react";
import { format } from "date-fns";

const EVENT_TABS = [
    { k: "", label: "All", color: "text-slate-300" },
    { k: "sent", label: "Sent", icon: PaperPlaneTilt, color: "text-slate-300" },
    { k: "delivered", label: "Delivered", icon: CheckCircle, color: "text-emerald-300" },
    { k: "opened", label: "Opened", icon: EnvelopeOpen, color: "text-cyan-300" },
    { k: "reopened", label: "Re-opened", icon: Eye, color: "text-blue-300" },
    { k: "clicked", label: "Clicked", icon: Cursor, color: "text-orange-300" },
    { k: "bounced", label: "Bounced", icon: Warning, color: "text-red-300" },
    { k: "failed", label: "Failed", icon: XCircle, color: "text-red-400" },
    { k: "unsubscribed", label: "Unsubscribed", icon: XCircle, color: "text-slate-400" },
];

/** Style maps — avoid nested ternaries in JSX for readability. */
const CAMPAIGN_STATUS_STYLES = {
    sending: "bg-orange-500/20 text-orange-300 animate-pulse",
    sent: "bg-emerald-500/20 text-emerald-300",
    failed: "bg-red-500/20 text-red-300",
    scheduled: "bg-blue-500/20 text-blue-300",
};
const CAMPAIGN_STATUS_DEFAULT = "bg-slate-700 text-slate-300";
const campaignStatusClass = (s) => CAMPAIGN_STATUS_STYLES[s] || CAMPAIGN_STATUS_DEFAULT;

const EVENT_PILL_STYLES = {
    opened: "bg-cyan-500/15 text-cyan-300",
    reopened: "bg-blue-500/15 text-blue-300",
    clicked: "bg-orange-500/15 text-orange-300",
    bounced: "bg-red-500/15 text-red-300",
    failed: "bg-red-500/25 text-red-200",
    delivered: "bg-emerald-500/15 text-emerald-300",
};
const EVENT_PILL_DEFAULT = "bg-slate-700 text-slate-300";
const eventPillClass = (t) => EVENT_PILL_STYLES[t] || EVENT_PILL_DEFAULT;

function campaignTimestampLabel(c) {
    if (c.sent_at) return `Sent ${format(new Date(c.sent_at), "MMM d, HH:mm")}`;
    if (c.scheduled_at) return `Scheduled ${format(new Date(c.scheduled_at), "MMM d, HH:mm")}`;
    return "Draft";
}

function StatBox({ label, value, accent = "cyan", rate }) {
    const colors = {
        cyan: "text-cyan-300 border-cyan-500/30",
        orange: "text-orange-300 border-orange-500/30",
        emerald: "text-emerald-300 border-emerald-500/30",
        red: "text-red-300 border-red-500/30",
        slate: "text-slate-300 border-slate-700",
    }[accent];
    return (
        <div className={`tech-card p-4 border ${colors}`}>
            <div className="overline">{label}</div>
            <div className="text-2xl font-heading font-bold text-slate-50 font-mono-alt mt-1">{value}</div>
            {rate != null && <div className="text-xs text-slate-400 mt-1 font-mono-alt">{rate}%</div>}
        </div>
    );
}

export default function CampaignDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [c, setC] = useState(null);
    const [events, setEvents] = useState([]);
    const [tab, setTab] = useState("");
    const [eventsTotal, setEventsTotal] = useState(0);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/campaigns/${id}`);
            setC(data);
            const er = await api.get(`/admin/campaigns/${id}/events`, { params: tab ? { event_type: tab } : {} });
            setEvents(er.data.items);
            setEventsTotal(er.data.total);
        } catch {
            toast.error("Failed to load campaign");
        }
    }, [id, tab]);

    useEffect(() => { load(); }, [load]);

    // Poll while sending
    useEffect(() => {
        if (c?.status === "sending") {
            const t = setInterval(load, 2500);
            return () => clearInterval(t);
        }
    }, [c?.status, load]);

    useEffect(() => { document.title = c ? `Surveys2CAD | ${c.name}` : "Surveys2CAD | Campaign"; }, [c]);

    if (!c) return <div className="p-10 text-slate-400">Loading…</div>;

    const s = c.stats || {};
    const rate = (n) => s.sent ? ((n || 0) / s.sent * 100).toFixed(1) : 0;

    const exportEvents = async () => {
        try {
            const res = await api.get(`/admin/campaigns/${id}/events/export`, {
                params: tab ? { event_type: tab } : {},
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `campaign-${c.name.replace(/\s+/g, "_")}-events.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Export failed");
        }
    };

    const sendNow = async () => {
        if (!window.confirm(`Send to ${s.total} recipients?`)) return;
        try {
            await api.post(`/admin/campaigns/${id}/send`);
            toast.success("Sending — analytics updating in real-time");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="campaign-detail">
            <button onClick={() => nav("/admin/campaigns")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 mb-4">
                <ArrowLeft size={16} /> Campaigns
            </button>

            <div className="flex items-end justify-between flex-wrap gap-3 mb-6 sm:mb-8">
                <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-mono-alt uppercase ${campaignStatusClass(c.status)}`}>
                            {c.status}
                        </span>
                        <span className="text-xs text-slate-500 font-mono-alt">
                            {campaignTimestampLabel(c)}
                        </span>
                    </div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50 tracking-tight break-words">{c.name}</h1>
                    <p className="text-slate-400 mt-1 text-sm break-words">{c.subject}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {(c.status === "draft" || c.status === "failed") && (
                        <>
                            <Link to={`/admin/campaigns/new`} state={{ cloneFrom: c.id }} className="btn-ghost"><PencilSimple size={16} /> Edit</Link>
                            <button onClick={sendNow} className="btn-primary"><PaperPlaneTilt size={16} weight="bold" /> Send now</button>
                        </>
                    )}
                </div>
            </div>

            {/* Stats grid — scales from 2 cols (mobile) → 4 cols (tablet) → 8 cols (desktop) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3 mb-6 sm:mb-8" data-testid="campaign-stats">
                <StatBox label="Total" value={s.total || 0} accent="slate" />
                <StatBox label="Sent" value={s.sent || 0} />
                <StatBox label="Delivered" value={s.delivered || 0} accent="emerald" />
                <StatBox label="Opened" value={s.opened || 0} accent="cyan" rate={rate(s.opened)} />
                <StatBox label="Re-opened" value={s.reopened || 0} accent="cyan" />
                <StatBox label="Clicked" value={s.clicked || 0} accent="orange" rate={rate(s.clicked)} />
                <StatBox label="Bounced" value={s.bounced || 0} accent="red" rate={rate(s.bounced)} />
                <StatBox label="Failed" value={s.failed || 0} accent="red" />
            </div>

            {/* Events */}
            <div className="mb-4 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline mb-2">Event stream</div>
                    <h2 className="font-heading text-xl font-semibold text-slate-50">Real-time activity ({eventsTotal})</h2>
                </div>
                <button onClick={exportEvents} className="btn-ghost text-xs" data-testid="export-events-btn">
                    <DownloadSimple size={14} /> Export {tab || "all"} events
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                {EVENT_TABS.map((t) => (
                    <button
                        key={t.k || "all"}
                        onClick={() => setTab(t.k)}
                        className={`px-3 py-1.5 rounded-full text-xs font-heading font-medium flex items-center gap-1.5 ${
                            tab === t.k ? "bg-cyan-500/15 border border-cyan-400/50 text-cyan-200"
                                        : "bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-slate-200"
                        }`}
                        data-testid={`event-tab-${t.k || "all"}`}
                    >
                        {t.icon && <t.icon size={12} />} {t.label}
                    </button>
                ))}
            </div>

            <div className="tech-card overflow-hidden">
                {events.length === 0 ? (
                    <div className="p-10 text-center text-slate-500">No events yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-800 bg-slate-900/40">
                                    {["Time", "Event", "Email", "Details"].map((h) => (
                                        <th key={h} className="text-left px-4 py-3 overline">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((e) => (
                                    <tr key={e.id} className="border-b border-slate-800/60 hover:bg-slate-900/30">
                                        <td className="px-4 py-2.5 text-slate-400 font-mono-alt text-xs whitespace-nowrap">
                                            {e.timestamp ? format(new Date(e.timestamp), "MMM d, HH:mm:ss") : "—"}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-mono-alt ${eventPillClass(e.event_type)}`}>
                                                {e.event_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-100 font-mono-alt text-xs">{e.email || "—"}</td>
                                        <td className="px-4 py-2.5 text-slate-400 text-xs truncate max-w-md">
                                            {e.meta?.url || e.meta?.ua || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
